<template>
  <div class="video-player-MpegPlayer-king-container">
    <video ref="myPlayerDom" class="MpegPlayer-style" :controls="videoConfig.controls" :muted="videoConfig.muted" :autoplay="videoConfig.autoplay"></video>
  </div>
</template>

<script>
import MpegPlayer from "mpegts.js";

export default {
  name: "MpegVideoPlayer",
  props: {
    idIndex: {
      type: Number,
      default: 999,
    },
    videoUrl: {
      type: String,
      required: true,
    },
    videoConfigFa: {
      type: Object,
      default: () => ({}),
    },
    configFa: {
      type: Object,
      default: () => ({}),
    },
    configBasicFa: {
      type: Object,
      default: () => ({}),
    },
    playbackRate: {
      type: Number,
      default: 1,
      validator: val => [1, 2, 4, 6, 8].includes(val),
    },
    // 流类型：flv / m3u8（HLS）
    streamType: {
      type: String,
      default: "flv",
      validator: val => ["flv", "m3u8", "hls"].includes(val),
    },
  },
  data() {
    return {
      myPlayerObj: null,
      errorFlag: false,
      hasEverPlayed: false,
      errorCount: 0,
      MAX_ERROR_COUNT: 3,
      animationFrameId: null,
      // 自动重连相关
      reconnectTimer: null,
      reconnectAttempts: 0,
      maxReconnectAttempts: 10, // 最大重连次数
      baseReconnectDelay: 1500, // 基础重连延迟（毫秒）
      isReconnecting: false, // 是否正在重连中
      isDestroyed: false, // 组件是否已销毁（防止销毁后重连）
      defaultVideoConfig: {
        muted: true,
        autoplay: true,
        controls: false,
      },
      defaultConfig: {
        hasAudio: false,
        hasVideo: true,
        isLive: true,
      },
      defaultConfigBasic: {
        enableWorker: true,
        enableStashBuffer: true,
        autoCleanupSourceBuffer: true,
        reuseRedirectedURL: true,
        fixAudioTimestampGap: true,
        seekType: "range",
        autoLoad: true,
        statisticsInfoReportInterval: 3000,
      },
      // 网速监控相关
      speedMonitor: {
        lastBytes: 0, // 上次统计的字节数
        lastTime: 0, // 上次统计的时间戳
        timer: null, // 定时器
        interval: 1000, // 监控间隔（毫秒）
      },
    };
  },
  computed: {
    videoConfig() {
      return Object.assign({}, this.defaultVideoConfig, this.videoConfigFa);
    },
  },
  watch: {
    videoUrl: {
      handler() {
        // 切换视频源时重置所有状态
        this.isDestroyed = false;
        this.isReconnecting = false;
        this.reconnectAttempts = 0;
        this.hasEverPlayed = false;
        this.errorFlag = false;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        this.destroyPlayer();
        this.$nextTick(() => {
          this.init();
        });
      },
    },
    playbackRate: {
      handler(newRate) {
        const video = this.$refs.myPlayerDom;
        if (video) {
          video.playbackRate = newRate;
        }
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.init();
    });
  },
  methods: {
    init() {
      if (!MpegPlayer.isSupported()) {
        this.errorFlag = true;
        this.$emit("player-created", {
          index: this.idIndex,
          player: null,
          err: false,
        });
        return;
      }

      const video = this.$refs.myPlayerDom;
      if (!video) {
        console.warn("[MpegVideoPlayer] video 元素不存在，延迟初始化");
        return;
      }

      // 如果是重连，先重置 video 元素的错误状态
      if (this.isReconnecting) {
        this.resetVideoElement(video);
      }

      const config = Object.assign({}, this.defaultConfig, this.configFa);
      const configBasic = Object.assign({}, this.defaultConfigBasic, this.configBasicFa);

      // mpegts.js 中 HLS 的类型值是 "m3u8"
      const typeValue = this.streamType === "hls" ? "m3u8" : this.streamType;

      try {
        this.myPlayerObj = MpegPlayer.createPlayer(
          {
            type: typeValue,
            url: this.videoUrl,
            ...config,
          },
          {
            ...configBasic,
          }
        );
      } catch (err) {
        console.error("[MpegVideoPlayer] 创建播放器失败", err);
        this.handleCreatePlayerError();
        return;
      }

      this.myPlayerObj.attachMediaElement(video);
      this.myPlayerObj.load();

      // play() 返回 promise，处理可能的拒绝
      const playPromise = this.myPlayerObj.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(err => {
          console.warn("[MpegVideoPlayer] play() 被浏览器拒绝（可能需要用户交互）", err);
          // 如果是 NotAllowedError，不视为致命错误，后续可能恢复
          if (err.name === "NotAllowedError") {
            this.$emit("player-created", {
              index: this.idIndex,
              player: this.myPlayerObj,
              err: false,
            });
          }
        });
      }

      video.playbackRate = this.playbackRate;

      // 添加统计信息监听
      this.myPlayerObj.on(MpegPlayer.Events.STATISTICS_INFO, stat => {
        this.handleStatistics(stat);
      });

      // 恢复数据流监听（用于重连后检测恢复）
      this.myPlayerObj.on(MpegPlayer.Events.LOADING_COMPLETE, () => {
        console.log("[MpegVideoPlayer] 数据加载完成");
        this.reconnectAttempts = 0; // 成功加载后重置重连计数
      });

      // 精准错误监听 - 区分不同错误类型进行处理
      this.myPlayerObj.on(MpegPlayer.Events.ERROR, (type, detail) => {
        console.warn("[mpegts.js ERROR] 类型:", type, "详情:", detail);

        if (type === MpegPlayer.ErrorTypes.MEDIA_ERROR) {
          // MSE 错误（SourceBuffer 错误）- 需要销毁并重建播放器
          console.warn("[MpegVideoPlayer] MSE MediaError，将进行完全重建");
          this.handleMSEError();
        } else if (type === MpegPlayer.ErrorTypes.NETWORK_ERROR) {
          // 网络错误 - 可能只是暂时断流，尝试重连
          console.warn("[MpegVideoPlayer] 网络错误，尝试重连");
          this.scheduleReconnect();
        } else {
          // 其他错误类型，记录日志但不立即处理
          console.warn("[MpegVideoPlayer] 其他错误类型:", type);
        }
      });

      // video 标签兜底监听 - 区分错误级别
      video.addEventListener("error", e => {
        console.warn("[video error]", video.error, e);
        // 检查是否是 MSE 相关的 media error
        const mediaError = video.error;
        if (mediaError && mediaError.code === MediaError.MEDIA_ERR_DECODE) {
          // 解码错误 - 需要完全重建
          this.handleMSEError();
        } else {
          // 其他 video 错误，尝试重连
          this.scheduleReconnect();
        }
      });

      // 非致命提示，仅记录日志
      video.addEventListener("waiting", () => console.warn("[video] 等待数据中 (waiting)"));
      video.addEventListener("stalled", () => console.warn("[video] 数据加载中断 (stalled)"));

      // 播放成功监听（只抛一次）
      video.addEventListener("canplay", () => {
        if (!this.hasEverPlayed) {
          this.hasEverPlayed = true;
          this.errorFlag = false;
          this.isReconnecting = false;
          this.reconnectAttempts = 0; // 播放成功，重置重连计数
          this.$emit("player-created", {
            index: this.idIndex,
            player: this.myPlayerObj,
            err: true,
          });
          console.log("✅ 播放成功 canplay");
          this.startSpeedMonitor();
        }
      });

      // 可选兜底检测
      this.checkVideoPlaySuccess(video);
    },

    /**
     * 处理 MSE 错误（SourceBuffer 故障）
     * 需要彻底销毁播放器，重置 video 元素，然后重建
     */
    handleMSEError() {
      if (this.isDestroyed) return;

      // 防止重复处理
      if (this.isReconnecting) {
        console.warn("[MpegVideoPlayer] 已在重连中，跳过重复触发");
        return;
      }

      console.warn("[MpegVideoPlayer] 开始处理 MSE 错误，将完全重建播放器");
      this.isReconnecting = true;

      // 先彻底销毁当前播放器
      this._forceDestroyPlayer();

      // 通知父组件
      this.$emit("player-created", {
        index: this.idIndex,
        player: null,
        err: false,
      });

      // 延迟后重连（给浏览器时间清理资源）
      this.scheduleReconnect();
    },

    /**
     * 处理创建播放器失败的情况
     */
    handleCreatePlayerError() {
      this.errorFlag = true;
      this.$emit("player-created", {
        index: this.idIndex,
        player: null,
        err: false,
      });
      // 尝试重连
      this.scheduleReconnect();
    },

    /**
     * 调度重连（带指数退避）
     */
    scheduleReconnect() {
      if (this.isDestroyed) return;

      this.reconnectAttempts++;

      if (this.reconnectAttempts > this.maxReconnectAttempts) {
        console.error("[MpegVideoPlayer] 已达到最大重连次数，停止重连");
        this.errorFlag = true;
        this.isReconnecting = false;
        this.$emit("player-created", {
          index: this.idIndex,
          player: null,
          err: false,
        });
        this.$emit("reconnect-failed", {
          index: this.idIndex,
          attempts: this.reconnectAttempts,
        });
        return;
      }

      // 清除已有定时器
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
      }

      // 指数退避：1.5s, 3s, 6s, 12s... 最大 30s
      const delay = Math.min(
        this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
        30000
      );

      console.log(
        `[MpegVideoPlayer] 将在 ${(delay / 1000).toFixed(1)}s 后进行第 ${this.reconnectAttempts} 次重连`
      );

      this.reconnectTimer = setTimeout(() => {
        if (this.isDestroyed) return;
        console.log(`[MpegVideoPlayer] 开始第 ${this.reconnectAttempts} 次重连`);
        this.init();
      }, delay);
    },

    /**
     * 重置 video 元素错误状态
     * 当 MSE 错误发生后，video.error 被设置，需要清除才能重新 appendBuffer
     */
    resetVideoElement(video) {
      if (!video) return;
      try {
        // 方法1：通过设置空 src 并 load() 来清除错误状态
        video.removeAttribute("src");
        video.load();
        console.log("[MpegVideoPlayer] video 元素错误状态已重置");
      } catch (e) {
        console.warn("[MpegVideoPlayer] 重置 video 元素失败", e);
      }
    },

    /**
     * 强制销毁播放器（同步方式，用于 MSE 错误恢复）
     */
    _forceDestroyPlayer() {
      // 清除检查定时器
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      if (!this.myPlayerObj) return;

      const player = this.myPlayerObj;
      this.myPlayerObj = null;

      try {
        // 移除事件监听（避免 destroy 后触发旧事件）
        player.off(MpegPlayer.Events.STATISTICS_INFO);
        player.off(MpegPlayer.Events.ERROR);
        player.off(MpegPlayer.Events.LOADING_COMPLETE);

        // 停止播放
        try { player.pause(); } catch (e) { /* ignore */ }
        try { player.unload(); } catch (e) { /* ignore */ }

        // 分离媒体元素
        try {
          player.detachMediaElement();
        } catch (e) {
          console.warn("[MpegVideoPlayer] detachMediaElement 失败", e);
        }

        // 销毁播放器
        try {
          player.destroy();
        } catch (e) {
          console.warn("[MpegVideoPlayer] destroy 失败", e);
        }
      } catch (e) {
        console.error("[MpegVideoPlayer] 强制销毁播放器时出错", e);
      }
    },

    checkVideoPlaySuccess(video) {
      let checkCount = 0;
      const maxCheck = 30;
      const interval = 300;
      const check = () => {
        if (this.isDestroyed) return;
        if (!video) return;
        const canPlay = video.readyState >= 3;
        if (canPlay && !this.hasEverPlayed) {
          this.hasEverPlayed = true;
          this.errorFlag = false;
          this.isReconnecting = false;
          this.reconnectAttempts = 0;
          this.$emit("player-created", {
            index: this.idIndex,
            player: this.myPlayerObj,
            err: true,
          });
          this.startSpeedMonitor();
        } else {
          checkCount++;
          if (checkCount < maxCheck) {
            setTimeout(() => {
              if (!this.isDestroyed) {
                this.animationFrameId = requestAnimationFrame(check);
              }
            }, interval);
          } else {
            console.warn("播放源监测超时，检查播放源是否正常");
            // 超时也尝试重连
            if (!this.hasEverPlayed && !this.isReconnecting) {
              this.scheduleReconnect();
            }
          }
        }
      };
      check();
    },

    destroyPlayer() {
      // 清除重连定时器
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      // 重置 video 元素
      const video = this.$refs.myPlayerDom;
      if (video) {
        try {
          video.removeAttribute("src");
          // 移除 video 级事件监听
          video.onerror = null;
        } catch (e) {
          /* ignore */
        }
      }

      if (this.myPlayerObj) {
        try {
          this.myPlayerObj.off(MpegPlayer.Events.STATISTICS_INFO);
          this.myPlayerObj.off(MpegPlayer.Events.ERROR);
          this.myPlayerObj.off(MpegPlayer.Events.LOADING_COMPLETE);

          this.myPlayerObj.pause();
          this.myPlayerObj.unload();
          this.myPlayerObj.detachMediaElement();
          setTimeout(() => {
            try {
              this.myPlayerObj && this.myPlayerObj.destroy();
            } catch (e) {
              console.warn("MpegPlayer destroy 出现异常", e);
            }
            this.myPlayerObj = null;
          }, 100);
        } catch (e) {
          console.error("销毁播放器失败", e);
          this.myPlayerObj = null;
        }
      }
    },
    // 启动网速监控
    startSpeedMonitor() {
      // 不再使用轮询，因为我们使用事件监听
      console.log("网速监控已启动（基于事件监听）");
    },
    // 停止网速监控
    stopSpeedMonitor() {
      if (this.speedMonitor.timer) {
        clearInterval(this.speedMonitor.timer);
        this.speedMonitor.timer = null;
      }
    },
    // 计算网速 - 这个方法现在只作为备选方案
    calculateSpeed() {
      if (!this.myPlayerObj) return;
      try {
        // mpegts.js 中获取统计信息的方法
        const stats = this.myPlayerObj.getStatistics ? this.myPlayerObj.getStatistics() : null;
        this.handleStatistics(stats || {});
      } catch (error) {
        console.warn("[网速监控] 计算网速时出错", error);
      }
    },
    // 处理统计信息的新方法
    handleStatistics(stat) {
      if (!stat) {
        console.warn("[网速监控] 统计信息为空");
        return;
      }

      // 直接处理统计信息，不需要轮询
      // mpegts.js 中字节数统计字段名可能是 loadedByteCount 或其他
      const currentBytes = stat.loadedByteCount || stat.bytesReceived || stat.totalBytesReceived || stat.loaded || 0;

      if (currentBytes > 0) {
        const currentTime = Date.now();
        const timeDiff = (currentTime - this.speedMonitor.lastTime) / 1000; // 转换为秒
        const bytesDiff = currentBytes - this.speedMonitor.lastBytes;

        if (timeDiff > 0 && bytesDiff > 0) {
          // 计算网速（字节/秒）
          const bytesPerSecond = bytesDiff / timeDiff;
          // 转换为可读格式
          const speedInfo = this.formatSpeed(bytesPerSecond);

          // 发送网速事件给父组件
          this.$emit("network-speed", {
            index: this.idIndex,
            rawSpeed: bytesPerSecond, // 原始速度（字节/秒）
            ...speedInfo, // formattedSpeed: 格式化后的速度, unit: 单位
          });

          // 调试日志
          console.debug(`[事件监听网速监控] 索引:${this.idIndex}, 速度:${speedInfo.formattedSpeed} ${speedInfo.unit}, bytes: ${currentBytes}`);
        }

        // 更新统计数据
        this.speedMonitor.lastBytes = currentBytes;
        this.speedMonitor.lastTime = currentTime;
      } else {
        // 如果没有字节数据，尝试从其他字段获取
        if (stat.speed) {
          // 如果 stat 对象包含 speed 字段，直接使用
          const speedInfo = this.formatSpeed(stat.speed * 1024); // 假设 speed 单位是 KB/s

          this.$emit("network-speed", {
            index: this.idIndex,
            rawSpeed: stat.speed * 1024, // 转换为字节/秒
            ...speedInfo,
          });

          console.debug(`[事件监听网速监控] 索引:${this.idIndex}, 速度:${speedInfo.formattedSpeed} ${speedInfo.unit}, 来自 speed 字段`);
        }
      }
    },
    // 格式化网速为可读格式
    formatSpeed(bytesPerSecond) {
      if (bytesPerSecond < 1024) {
        return {
          formattedSpeed: bytesPerSecond.toFixed(2),
          unit: "B/s",
        };
      } else if (bytesPerSecond < 1024 * 1024) {
        return {
          formattedSpeed: (bytesPerSecond / 1024).toFixed(2),
          unit: "KB/s",
        };
      } else {
        return {
          formattedSpeed: (bytesPerSecond / (1024 * 1024)).toFixed(2),
          unit: "MB/s",
        };
      }
    },
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.stopSpeedMonitor();
    this.destroyPlayer();
  },
};
</script>

<style scoped>
.video-player-MpegPlayer-king-container {
  min-height: 169px;
  min-width: 300px;
  width: 100%;
  height: 100%;
  background: #000;
  position: relative;
}
.MpegPlayer-style {
  width: 100% !important;
  height: 100% !important;
}
</style>
